import { FocusEvent, MouseEvent, type ReactNode } from 'react'
import classNames from 'classnames/bind'

import { emptyObject } from 'app/common/lib/empty'

import { SurfaceType } from '../plain-dropdown-surface'
import styles from './plain-button-as-link.module.scss'

const cx = classNames.bind(styles)

export type Props = {
  children: ReactNode
  type?: 'button' | 'submit' // 'reset' type forbidden, do not use
  id?: string
  disabled?: boolean
  className?: string
  linkClassName?: string
  tabIndex?: string
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>
  onFocus?: (e: FocusEvent<HTMLButtonElement>) => void
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void
  ariaHaspopup?: SurfaceType
  'aria-haspopup'?: SurfaceType
  ariaExpanded?: 'true' | 'false'
  'aria-expanded'?: 'true' | 'false'
  ariaLabel?: string
  'aria-label'?: string
}

/**
 * Компонент кнопки в стиле ссылки.
 * Используется для ссылок, не ведущих на другую страницу.
 *
 */
const PlainButtonAsLink = ({
  children,
  type,
  id,
  disabled,
  tabIndex,
  onClick,
  className,
  linkClassName,
  ariaHaspopup,
  ariaExpanded,
  ariaLabel,
  onFocus,
  onBlur,
  ...other
}: Props) => {
  const props = {
    id,
    onClick,
    disabled,
    className: cx(styles.root, className),
    'aria-haspopup': ariaHaspopup || other['aria-haspopup'],
    'aria-expanded': ariaExpanded || other['aria-expanded'],
    'aria-label': ariaLabel || other['aria-label'],
    onFocus,
    onBlur,
    ...(tabIndex ? { tabIndex: parseInt(tabIndex, 10) } : emptyObject),
  }

  const childrenContainer = (
    <span className={cx({ link: !disabled, disabled }, linkClassName)}>{children}</span>
  )

  // type must be static, so:
  if (type === 'submit') {
    return (
      <button type="submit" {...props}>
        {childrenContainer}
      </button>
    )
  }

  // default type: "button"
  return (
    <button type="button" {...props}>
      {childrenContainer}
    </button>
  )
}

export default PlainButtonAsLink
